import React, { useEffect, useMemo, useRef } from "react";
import L from "leaflet";

const OSM_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const SEAMARK_URL = "https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png";
const MV_COLOR = "#1E88E5";

const toMs = (p) => (p && p.ts ? Date.parse(p.ts) : NaN);
const latlngs = (pts) => pts.map((p) => [p.lat, p.lng]);

// Indice dell'ultimo punto raggiunto: per tempo (se tutti i punti hanno ts) o per frazione.
function reachedIndex(pts, progress, t0, t1, timeBased) {
  if (!pts.length) return -1;
  if (!timeBased) return Math.min(pts.length - 1, Math.max(0, Math.floor(progress * (pts.length - 1))));
  const now = t0 + progress * (t1 - t0);
  let idx = -1;
  for (let i = 0; i < pts.length; i++) {
    if (toMs(pts[i]) <= now + 1) idx = i; else break;
  }
  return idx;
}

// Replay map for archive: traccia naufrago (rosso/oro) + traccia motovedetta assegnata (blu),
// sincronizzate sulla stessa linea temporale.
export default function ReplayMap({ track, mvTrack = [], progress, mvName }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const refs = useRef({});

  const timeline = useMemo(() => {
    const all = [...(track || []), ...(mvTrack || [])];
    const times = all.map(toMs);
    const timeBased = times.length > 0 && times.every((t) => Number.isFinite(t));
    if (!timeBased) return { timeBased: false, t0: 0, t1: 1 };
    const t0 = Math.min(...times);
    const t1 = Math.max(...times);
    return { timeBased: t1 > t0, t0, t1 };
  }, [track, mvTrack]);

  useEffect(() => {
    if (mapRef.current || !containerRef.current || !track || !track.length) return;
    const map = L.map(containerRef.current, { center: [track[0].lat, track[0].lng], zoom: 11 });
    L.tileLayer(OSM_URL, { maxZoom: 18 }).addTo(map);
    L.tileLayer(SEAMARK_URL, { maxZoom: 18, opacity: 0.9 }).addTo(map);
    const r = refs.current;
    r.line = L.polyline(latlngs(track), { color: "#1E5AA8", weight: 2, opacity: 0.5, dashArray: "4 6" }).addTo(map);
    r.doneLine = L.polyline([], { color: "#E8B923", weight: 4 }).addTo(map);
    r.marker = L.circleMarker([track[0].lat, track[0].lng], {
      radius: 8, color: "#fff", weight: 2, fillColor: "#D32F2F", fillOpacity: 1,
    }).addTo(map).bindTooltip("Naufrago", { direction: "top" });
    const mv = mvTrack || [];
    if (mv.length) {
      r.mvLine = L.polyline(latlngs(mv), { color: MV_COLOR, weight: 2, opacity: 0.4, dashArray: "2 6" }).addTo(map);
      r.mvDoneLine = L.polyline([], { color: MV_COLOR, weight: 4 }).addTo(map);
      r.mvMarker = L.marker([mv[0].lat, mv[0].lng], {
        icon: L.divIcon({ className: "", html: '<div data-testid="replay-mv-marker" class="sgc-replay-mv"></div>', iconSize: [18, 18], iconAnchor: [9, 9] }),
        opacity: 0,
      }).addTo(map).bindTooltip(mvName ? `Motovedetta ${mvName}` : "Motovedetta", { direction: "top" });
    }
    map.fitBounds(latlngs([...track, ...mv]), { padding: [30, 30] });
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => {
      map.remove();
      mapRef.current = null;
      refs.current = {};
    };
  }, [track, mvTrack, mvName]);

  useEffect(() => {
    const r = refs.current;
    if (!mapRef.current || !track || !track.length || !r.marker) return;
    const { timeBased, t0, t1 } = timeline;
    const idx = Math.max(0, reachedIndex(track, progress, t0, t1, timeBased));
    r.marker.setLatLng([track[idx].lat, track[idx].lng]);
    r.doneLine.setLatLngs(latlngs(track.slice(0, idx + 1)));
    if (r.mvMarker) {
      const mv = mvTrack || [];
      const mi = reachedIndex(mv, progress, t0, t1, timeBased);
      if (mi < 0) {
        r.mvMarker.setOpacity(0);
        r.mvDoneLine.setLatLngs([]);
      } else {
        r.mvMarker.setOpacity(1);
        r.mvMarker.setLatLng([mv[mi].lat, mv[mi].lng]);
        r.mvDoneLine.setLatLngs(latlngs(mv.slice(0, mi + 1)));
      }
    }
  }, [progress, track, mvTrack, timeline]);

  return <div ref={containerRef} data-testid="replay-map" className="w-full h-[420px] rounded-sm overflow-hidden" />;
}
